/**
 * Push notification setup for Capacitor native apps.
 * Requests permission, registers the device token with Supabase,
 * and handles incoming notifications.
 *
 * On web: no-ops silently.
 * On iOS: uses APNs (requires Apple Developer account + cert)
 * On Android: uses Firebase Cloud Messaging (requires google-services.json)
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';

export async function initPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;

  // Request permission
  const { receive } = await PushNotifications.requestPermissions();
  if (receive !== 'granted') {
    console.log('Push permission denied');
    return;
  }

  // Register with APNs / FCM
  await PushNotifications.register();

  // Store device token in Supabase when received
  PushNotifications.addListener('registration', async ({ value: token }) => {
    console.log('Push token:', token);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Upsert the device token so we can send targeted pushes later
    await supabase.from('push_tokens' as any).upsert({
      user_id: user.id,
      token,
      platform: Capacitor.getPlatform(), // 'ios' | 'android'
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,platform' });
  });

  // Handle registration errors
  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error);
  });

  // Handle foreground notifications (app is open)
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push received (foreground):', notification);
    // Could show an in-app toast here
  });

  // Handle notification tap (app was in background/killed)
  PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const data = notification.data;
    // Navigate to the relevant page based on notification data
    if (data?.url) {
      window.location.href = data.url;
    } else if (data?.type === 'booking') {
      window.location.href = `/marketplace/trip/${data.booking_id}`;
    } else if (data?.type === 'message') {
      window.location.href = '/marketplace/messages';
    }
  });
}
